import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { FunnelPage } from './pages/FunnelPage';
import { Storefront } from './pages/Storefront';
import { CoursePage } from './pages/CoursePage';
import { Checkout, CheckoutSuccess } from './components/Checkout';
import { UnsubscribePage } from './pages/UnsubscribePage';

// Import page components
import { LandingPage } from './pages/LandingPage';
import { ThankYouView } from './components/ThankYouView';
import AdminDashboard from './components/AdminDashboard';

// Wrapper component to provide navigation callbacks to LandingPage
function LandingPageWrapper() {
    const navigate = React.useCallback((path) => {
        window.location.href = path;
    }, []);

    return (
        <LandingPage
            onNavigateToAdmin={() => navigate('/admin')}
            onNavigateToThankYou={() => navigate('/thank-you')}
        />
    );
}

// Wrapper for ThankYouView to provide navigation callbacks
function ThankYouWrapper() {
    const handleHomeClick = React.useCallback(() => {
        window.location.href = '/';
    }, []);

    const handleAdminClick = React.useCallback(() => {
        window.location.href = '/admin';
    }, []);

    const handleScroll = React.useCallback((id) => {
        // Navigate to home first, then scroll
        window.location.href = `/#${id}`;
    }, []);

    return (
        <ThankYouView
            onHomeClick={handleHomeClick}
            onAdminClick={handleAdminClick}
            onScrollTo={handleScroll}
        />
    );
}

// Wrapper for AdminDashboard to provide logout callback
function AdminWrapper() {
    const handleLogout = React.useCallback(() => {
        window.history.pushState({}, '', '/');
        window.location.href = '/';
    }, []);

    return <AdminDashboard onLogout={handleLogout} />;
}

// Wrapper for Checkout to retrieve items from session storage
function CheckoutWrapper() {
    const [items, setItems] = React.useState([]);

    React.useEffect(() => {
        // Get checkout items from session storage (set by Storefront)
        const storedItems = sessionStorage.getItem('checkoutItems');
        if (storedItems) {
            try {
                const parsedItems = JSON.parse(storedItems);
                setItems(parsedItems);
            } catch (e) {
                console.error('Error parsing checkout items:', e);
            }
        }
    }, []);

    const handleCancel = React.useCallback(() => {
        sessionStorage.removeItem('checkoutItems');
        sessionStorage.removeItem('checkoutCustomerEmail');
        sessionStorage.removeItem('checkoutCustomerName');
        // Check if we came from a funnel
        const funnelInfo = sessionStorage.getItem('funnelReturnInfo');
        if (funnelInfo) {
            try {
                const info = JSON.parse(funnelInfo);
                sessionStorage.removeItem('funnelReturnInfo');
                window.location.href = `/funnel/${info.slug}`;
            } catch (e) {
                window.location.href = '/storefront';
            }
        } else {
            window.location.href = '/storefront';
        }
    }, []);

    const handleSuccess = React.useCallback(() => {
        sessionStorage.removeItem('checkoutItems');
    }, []);

    return (
        <Checkout
            items={items}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
        />
    );
}

export function AppRouter() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <Routes>
                    {/* Main landing page */}
                    <Route path="/" element={<LandingPageWrapper />} />

                    {/* Admin dashboard */}
                    <Route path="/admin" element={<AdminWrapper />} />

                    {/* Thank you page after form submission */}
                    <Route path="/thank-you" element={<ThankYouWrapper />} />

                    {/* Funnel pages */}
                    <Route path="/funnel/:slug" element={<FunnelPage />} />

                    {/* Storefront */}
                    <Route path="/storefront" element={<Storefront />} />

                    {/* Course Page */}
                    <Route path="/courses/:courseId" element={<CoursePage />} />

                    {/* Checkout - PayPal payment flow */}
                    <Route path="/checkout" element={<CheckoutWrapper />} />
                    <Route path="/checkout/success" element={<CheckoutSuccess />} />
                    <Route path="/checkout/cancel" element={<Navigate to="/storefront" replace />} />

                    {/* Unsubscribe */}
                    <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

